'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useProduct } from '@/frontastic';
import { getSelectedDivision } from '@/helpers/utils/siteUtils';
// Create context
const ProductDetailsContext = createContext<{
  productData: any;
  loading: boolean;
  error: Error | null;
}>({
  productData: null,
  loading: false,
  error: null,
});

// Create hook to use the context
export const useProductDetails = () => {
  const context = useContext(ProductDetailsContext);
  if (!context) {
    throw new Error('useProductDetails must be used within a ProductDetailsProvider');
  }
  return context;
};

// Type definitions for props
interface ProductDetailsProviderProps {
  data: any;
  projectSettings: any;
  children: ReactNode;
}

export const ProductDetailsProvider = ({ data, projectSettings, children }: ProductDetailsProviderProps) => {
  const pathName = usePathname();
  if (pathName.indexOf('product') == -1) {
    return <>{children}</>;
  }
  const dataSource = data?.pageFolder?.dataSourceConfigurations.find(
    (source: any) => source?.type == 'frontastic/product',
  );
  const product = dataSource?.preloadedValue?.product;
  const { getProductGroups } = useProduct();
  const router = useRouter();
  const [productData, setProductData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (product?.productId) {
      // Reset states when product changes
      setLoading(true);
      setError(null);

      // Determine if we need to fetch multiple product groups

      if (product?.totalCount > 40) {
        fetchMultipleProductGroups(product?.totalCount);
      } else {
        fetchProductGroups();
      }
    }
  }, [product?.productId, projectSettings?.siteId, pathName]);

  const fetchProductGroups = () => {
    setLoading(true);
    getProductGroups({
      stiboDsku: product?.productId?.split('-')?.[0],
      productId: product?.productId || '',
      siteId: `${getSelectedDivision()}Site`,
      offset: 'NSRQ',
    })
      .then((productData: any) => {
        if (productData?.data?.products) {
          productData.data.products.hierarchalCategories = product?.hierarchalCategories;
        }
        processProductData(productData);
        setLoading(false);
      })
      .catch((error: any) => {
        console.error('Error while fetching product details', error);
        setError(new Error('Failed to fetch product details'));
        setLoading(false);
        // Only redirect on critical errors
        if (error.status >= 500) {
          router.push('/server-error');
        }
      });
  };

  const fetchMultipleProductGroups = async (totalCount: number) => {
    setLoading(true);
    try {
      let allProductData: any[] = [];
      let rangeStart = '';
      let totalProductFetched = 0;
      let isDiscontinued = false;

      const eachIterationCount = 40;
      let firstIteration = true;
      let total = 0;

      while (total < totalCount) {
        const response: any = await getProductGroups({
          stiboDsku: product?.productId?.split('-')?.[0],
          productId: product?.productId || '',
          siteId: `${getSelectedDivision()}Site`,
          offset: rangeStart,
        });

        const newProducts = response?.data?.products?.results || [];
        const existingKeys = new Set(allProductData.map((item: any) => item.key));
        const uniqueNewProducts = newProducts.filter((item: any) => !existingKeys.has(item.key));
        allProductData = [...allProductData, ...uniqueNewProducts];

        totalProductFetched += response?.data?.products?.total || totalProductFetched;
        isDiscontinued = isDiscontinued || response?.data?.products?.isDiscontinued;
        total += firstIteration ? eachIterationCount - 0 : eachIterationCount - 1;
        firstIteration = false;

        const productResultLength = response?.data?.products?.results?.length;
        if (productResultLength === 0) break; // Prevent infinite loop

        rangeStart = response?.data?.offsetId;
      }

      const mergedProductData = {
        data: {
          products: {
            totalProductFetched,
            results: allProductData,
            isDiscontinued,
            hierarchalCategories: product?.hierarchalCategories,
          },
        },
      };

      processProductData(mergedProductData);
      setLoading(false);
    } catch (err) {
      console.error('Error while fetching multiple product groups', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch product groups'));
      setLoading(false);

      // Only redirect on critical errors
      if (err && typeof err === 'object' && 'status' in err && (err as any).status >= 500) {
        router.push('/server-error');
      }
    }
  };
  const processProductData = (productData: any) => {
    setProductData(productData);
  };

  // Provide context value
  const contextValue = {
    productData,
    loading,
    error,
  };

  return <ProductDetailsContext.Provider value={contextValue}>{children}</ProductDetailsContext.Provider>;
};
